import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { shippoApi } from '../apis';
import { CreateShipmentDto, Rate } from '../apis/client-axios';

/**
 * Một biến thể = một shipment riêng bên Shippo. `extra` (bảo hiểm / chữ ký / khai báo pin) bắt buộc
 * phải gắn ngay lúc tạo shipment, và label sau đó phát thẳng từ rate id của shipment đó — nên không
 * có cách nào lấy nhiều tổ hợp bằng một call.
 */
export type TShipmentVariant = {
  /** Định danh biến thể: vừa là khoá tra kết quả, vừa là một phần của queryKey. */
  key: string;
  extra?: CreateShipmentDto['extra'];
};

export type TShipmentRatesResult = {
  /** rates theo `variant.key`; `undefined` = chưa có dữ liệu (đang fetch, bị disable, hoặc lỗi). */
  ratesByVariant: Record<string, Rate[] | undefined>;
  /** `messages` Shippo trả về theo `variant.key` — dùng để báo lý do khi không có rate nào. */
  messagesByVariant: Record<string, any[] | undefined>;
  isFetching: boolean;
  /** Lỗi đầu tiên gặp phải. Tham chiếu ổn định giữa các render nên dùng làm dep của effect được. */
  error: unknown;
};

/**
 * Lấy rate cho nhiều tổ hợp option cùng lúc, có cache theo nội dung shipment.
 *
 * Dùng `useQueries` thay cho `useMutation` vì mutation không cache: mỗi lần user đổi option (hoặc sửa
 * địa chỉ rồi sửa về như cũ) là bắn lại toàn bộ tổ hợp. Với query + `staleTime`, đổi option chỉ là đọc
 * cache — không loading; và kết quả gắn với `queryKey` tường minh nên không phải đoán ngược biến thể
 * từ `variables.extra` như trước.
 *
 * `base = null` hoặc `enabled = false` thì không gọi gì; nếu key đã có trong cache thì vẫn trả dữ liệu
 * cũ, nhờ vậy quay lại màn trước hiển thị tức thì.
 */
export const useShipmentRates = ({
  base,
  variants,
  enabled = true,
  staleTime = 3 * 60 * 1000,
  gcTime = 10 * 60 * 1000,
}: {
  base: CreateShipmentDto | null;
  variants: TShipmentVariant[];
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
}): TShipmentRatesResult => {
  const queries = useQueries({
    queries: variants.map((variant) => ({
      // react-query hash queryKey bằng stable-stringify (sắp xếp key) nên truyền thẳng object được.
      // `extra` BẮT BUỘC nằm trong key: cùng một biến thể nhưng khai báo pin đổi thì payload đổi,
      // nếu chỉ dựa vào `variant.key` thì react-query coi là trùng key và không fetch lại.
      queryKey: ['shipment-rates', base, variant.key, variant.extra ?? null] as const,
      queryFn: () =>
        shippoApi
          .shippoApiControllerCreateShipment(
            { ...(base as CreateShipmentDto), ...(variant.extra ? { extra: variant.extra } : {}) },
            { headers: { 'skip-error-handler': true } },
          )
          .then(({ data }) => ({
            rates: Object.values(data.rates ?? {}).filter(Boolean) as Rate[],
            messages: (data.messages ?? []) as any[],
          })),
      enabled: enabled && !!base,
      staleTime,
      gcTime,
      // Giữ đúng hành vi cũ (useMutation không tự retry) để 1 lỗi không thành 4 call sang Shippo.
      retry: false,
    })),
  });

  // Phụ thuộc trực tiếp vào các tham chiếu `data`: react-query giữ nguyên tham chiếu khi dữ liệu không
  // đổi, nên object trả về cũng ổn định — an toàn để làm dep cho effect ở component gọi.
  const queryData = queries.map((query) => query.data);

  const { ratesByVariant, messagesByVariant } = useMemo(() => {
    const rates: Record<string, Rate[] | undefined> = {};
    const messages: Record<string, any[] | undefined> = {};
    variants.forEach((variant, index) => {
      rates[variant.key] = queryData[index]?.rates;
      messages[variant.key] = queryData[index]?.messages;
    });
    return { ratesByVariant: rates, messagesByVariant: messages };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants.map((variant) => variant.key).join('|'), ...queryData]);

  return {
    ratesByVariant,
    messagesByVariant,
    isFetching: queries.some((query) => query.isFetching),
    error: queries.find((query) => query.error)?.error,
  };
};
